import { api } from './api';

export type KpiFormat = 'number' | 'percent' | 'currency';

export type KpiOperand =
  | { key: string; kind: 'account'; accountCodes?: string[]; accountNodeId?: string; dim?: Record<string, string[]> }
  | { key: string; kind: 'headcount'; department?: string }
  | { key: string; kind: 'kpi'; kpiId: string };

export interface Kpi {
  id: string;
  workspace_id: string;
  name: string;
  description: string | null;
  operands: KpiOperand[];
  formula: string;
  format: KpiFormat;
  target: number | null;
  scenario: string;
  reporting_currency: string | null;
  enabled: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export interface KpiInput {
  name: string;
  description?: string;
  operands: KpiOperand[];
  formula: string;
  format?: KpiFormat;
  target?: number | null;
  scenario: string;
  reportingCurrency?: string | null;
  enabled?: boolean;
}

export interface KpiPoint { periodStart: string; periodEnd: string; value: number | null; warnings: string[] }
export interface KpiResult {
  kpiId: string | null;
  name: string;
  format: KpiFormat;
  scenario: string;
  reportingCurrency: string;
  target: number | null;
  points: KpiPoint[];
  current: number | null;
  missingRates: { from: string; to: string }[];
}

export interface ComputeRange { periodStart: string; periodEnd: string; bucket?: 'month' | 'none' }

export const kpiApi = {
  list: async (wsId: string): Promise<Kpi[]> => (await api.get(`/api/workspaces/${wsId}/kpis`)).data,
  dashboard: async (wsId: string): Promise<KpiResult[]> => (await api.get(`/api/workspaces/${wsId}/kpis/dashboard`)).data,
  get: async (wsId: string, id: string): Promise<Kpi> => (await api.get(`/api/workspaces/${wsId}/kpis/${id}`)).data,
  create: async (wsId: string, body: KpiInput): Promise<Kpi> => (await api.post(`/api/workspaces/${wsId}/kpis`, body)).data,
  update: async (wsId: string, id: string, body: Partial<KpiInput>): Promise<Kpi> => (await api.patch(`/api/workspaces/${wsId}/kpis/${id}`, body)).data,
  remove: async (wsId: string, id: string): Promise<void> => { await api.delete(`/api/workspaces/${wsId}/kpis/${id}`); },
  compute: async (wsId: string, id: string, range: ComputeRange): Promise<KpiResult> => (await api.post(`/api/workspaces/${wsId}/kpis/${id}/compute`, range)).data,
  preview: async (wsId: string, body: KpiInput & ComputeRange): Promise<KpiResult> => (await api.post(`/api/workspaces/${wsId}/kpis/preview`, body)).data,
};

/** Format a KPI value for display by its format. */
export function formatKpiValue(value: number | null, format: KpiFormat, currency: string): string {
  if (value === null) return '—';
  if (format === 'percent') return `${(value * 100).toLocaleString(undefined, { maximumFractionDigits: 1 })}%`;
  if (format === 'currency') return `${value.toLocaleString(undefined, { maximumFractionDigits: 0 })} ${currency}`;
  return value.toLocaleString(undefined, { maximumFractionDigits: 2 });
}
